import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    RemoveAudienceMembership as RemoveAudienceMembershipMutation,
    RemoveAudienceMembershipVariables as RemoveAudienceMembershipMutationVariables,
    AudienceMemberDeleteInput,
    GetContactAudiences as GetContactAudiencesQuery,
    GetContactAudiencesVariables,
} from 'nutshell-graphql-types';

import REMOVE_AUDIENCE_MEMBERSHIP from '../mutations/remove-audience-membership.graphql';
import GET_CONTACT_AUDIENCES from '../queries/get-contact-audiences.graphql';

export const useRemoveAudienceMembership = (
    contactId: string
): {
    removeAudienceMembership: (input: AudienceMemberDeleteInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [removeAudienceMembership, {loading, error}] = useMutation<
        RemoveAudienceMembershipMutation,
        RemoveAudienceMembershipMutationVariables
    >(REMOVE_AUDIENCE_MEMBERSHIP);

    return {
        removeAudienceMembership: (input: AudienceMemberDeleteInput) => {
            return removeAudienceMembership({
                variables: {input},
                optimisticResponse: {
                    audienceMemberDelete: {
                        __typename: 'AudienceMemberDeletePayload',
                        audienceMember: {
                            __typename: 'AudienceMember',
                            id: input.audienceMemberId,
                        },
                    },
                },
                update: (proxy, {data}) => {
                    const cacheResult: GetContactAudiencesQuery | null | undefined =
                        proxy.readQuery<GetContactAudiencesQuery, GetContactAudiencesVariables>({
                            query: GET_CONTACT_AUDIENCES,
                            variables: {personId: contactId},
                        });
                    const removedAudienceMembership =
                        data && data.audienceMemberDelete.audienceMember;

                    if (cacheResult && cacheResult.contact && removedAudienceMembership) {
                        proxy.writeQuery({
                            query: GET_CONTACT_AUDIENCES,
                            variables: {personId: contactId},
                            data: {
                                ...cacheResult,
                                contact: {
                                    ...cacheResult.contact,
                                    audienceMembershipsSubscribed:
                                        cacheResult.contact.audienceMembershipsSubscribed.filter(
                                            (membership) =>
                                                membership.id !== removedAudienceMembership.id
                                        ),
                                },
                            },
                        });
                    }
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
